import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';

import Button from '../../../components/ui/Button';
import StatusIndicatorBadge from '../../../components/ui/StatusIndicatorBadge';

const RegistrationCard = ({ registration }) => {
  const navigate = useNavigate();

  const getActionButton = () => {
    switch (registration?.status) {
      case 'draft':
        return (
          <Button
            variant="default"
            size="sm"
            iconName="Edit"
            iconPosition="left"
            onClick={() => navigate('/team-registration-form', { state: { registrationId: registration?.dbId || registration?.id } })}
            fullWidth
          >Continue Draft
                      </Button>
        );
      case 'submitted':
      case 'pending':
        return (
          <Button
            variant="outline"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => navigate('/registration-detail-view', { state: { registrationId: registration?.dbId || registration?.id, userMode: true } })}
            fullWidth
          >
            View Details
          </Button>
        );
      case 'approved':
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={() => navigate('/declaration-and-submission', { state: { registrationId: registration?.dbId || registration?.id, viewMode: true } })}
              className="flex-1"
            >
              View
            </Button>
            <Button
              variant="success"
              size="sm"
              iconName="Download"
              iconPosition="left"
              onClick={() => window.open(registration?.declarationPdf, '_blank')}
              className="flex-1"
            >
              Download
            </Button>
          </div>
        );
      case 'rejected':
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="Eye"
              iconPosition="left"
              onClick={() => navigate('/declaration-and-submission', { state: { registrationId: registration?.dbId || registration?.id, viewMode: true } })}
              className="flex-1"
            >
              View
            </Button>
            <Button
              variant="default"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              onClick={() => navigate('/team-registration-form', { state: { registrationId: registration?.dbId || registration?.id } })}
              className="flex-1"
            >
              Resubmit
            </Button>
          </div>
        );
      default:
        return null;
    }
  };

  const getProgressColor = () => {
    if (registration?.completionPercentage >= 75) return 'bg-success';
    if (registration?.completionPercentage >= 50) return 'bg-warning';
    return 'bg-primary';
  };

  return (
    <div className="card hover:shadow-lg transition-all duration-200">
      <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
            <Icon name="Trophy" size={20} className="sm:w-6 sm:h-6" color="var(--color-primary)" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-foreground text-sm sm:text-base lg:text-lg truncate">
              {registration?.ageCategory}
            </h3>
            <p className="text-xs sm:text-sm text-muted-foreground truncate">{registration?.schoolName}</p>
          </div>
        </div>
        <StatusIndicatorBadge status={registration?.status} showIcon={true} className="flex-shrink-0" />
      </div>
      <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
        <div className="flex items-center gap-2 text-xs sm:text-sm">
          <Icon name="User" size={16} color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">Coach:</span>
          <span className="text-foreground font-medium">{registration?.coachName}</span>
        </div>

        <div className="flex items-center gap-2 text-xs sm:text-sm">
          <Icon name="Users" size={14} className="sm:w-4 sm:h-4" color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">Players:</span>
          <span className="text-foreground font-medium">
            {registration?.playerCount}/15 registered
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs sm:text-sm">
          <Icon name="Calendar" size={14} className="sm:w-4 sm:h-4" color="var(--color-muted-foreground)" />
          <span className="text-muted-foreground">Created:</span>
          <span className="text-foreground font-medium">{registration?.createdDate}</span>
        </div>

        {registration?.status === 'draft' && (
          <div className="space-y-1.5 sm:space-y-2 pt-2">
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <span className="text-muted-foreground">Completion</span>
              <span className="text-foreground font-semibold">
                {registration?.completionPercentage}%
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-1.5 sm:h-2 overflow-hidden">
              <div
                className={`h-full ${getProgressColor()} transition-all duration-300`}
                style={{ width: `${registration?.completionPercentage}%` }}
              />
            </div>
          </div>
        )}

        {registration?.status === 'rejected' && registration?.rejectionReason && (
          <div className="bg-error/10 border border-error/20 rounded-md p-2 sm:p-3 mt-2">
            <div className="flex items-start gap-2">
              <Icon name="AlertCircle" size={14} className="sm:w-4 sm:h-4 flex-shrink-0" color="var(--color-error)" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-error mb-1">Rejection Reason</p>
                <p className="text-xs text-error-foreground break-words">{registration?.rejectionReason}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      {getActionButton()}
    </div>
  );
};

export default RegistrationCard;